import { useState, useEffect } from "react";
import { FiUserPlus } from "react-icons/fi";
import RichTextEditor from "../common/RichTextEditor";

function ClientForm({ onSave, initialData = null, onCancel }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [address, setAddress] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || "");
      setEmail(initialData.email || "");
      setDob(initialData.dob || "");
      setPhone(initialData.phone || "");
      setGender(initialData.gender || "");
      setAddress(initialData.address || "");
      setEmergencyContact(initialData.emergencyContact || "");
      setEmergencyPhone(initialData.emergencyPhone || "");
      setNotes(initialData.notes || "");
    } else {
      setName("");
      setEmail("");
      setDob("");
      setPhone("");
      setGender("");
      setAddress("");
      setEmergencyContact("");
      setEmergencyPhone("");
      setNotes("");
    }
  }, [initialData]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!name || !email) return;
    onSave({
      id: initialData?.id,
      name,
      email,
      dob,
      phone,
      gender,
      address,
      emergencyContact,
      emergencyPhone,
      notes
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 p-0"
      style={{ minWidth: 700, width: "100%" }}
      autoComplete="off"
    >
      {/* Header (with icon and title) */}
      <div className="flex items-center gap-2 mb-4">
        <FiUserPlus className="text-blue-500 text-2xl" />
        <h2 className="text-xl font-bold tracking-tight">Add Client</h2>
      </div>

      {/* Personal Info Section */}
      <section className="rounded-xl border border-blue-100 bg-white shadow-sm p-5 mb-2">
        <div className="mb-4">
          <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-xs text-blue-700">
            <FiUserPlus className="text-blue-400" />
            Personal Info
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Name */}
          <div>
            <label className="block font-semibold text-xs text-blue-800 mb-1">Name</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          {/* Email */}
          <div>
            <label className="block font-semibold text-xs text-blue-800 mb-1">Email</label>
            <input
              type="email"
              className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          {/* DOB */}
          <div>
            <label className="block font-semibold text-xs text-blue-800 mb-1">Date of Birth</label>
            <input
              type="date"
              className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
              value={dob}
              onChange={e => setDob(e.target.value)}
            />
          </div>
          {/* Phone */}
          <div>
            <label className="block font-semibold text-xs text-blue-800 mb-1">Phone</label>
            <input
              type="tel"
              className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>
          {/* Gender */}
          <div>
            <label className="block font-semibold text-xs text-blue-800 mb-1">Gender</label>
            <select
              className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
              value={gender}
              onChange={e => setGender(e.target.value)}
            >
              <option value="">Select...</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
              <option>Prefer not to say</option>
            </select>
          </div>
          {/* Emergency Contact & Phone */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block font-semibold text-xs text-blue-800 mb-1">Emergency Contact</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
                value={emergencyContact}
                onChange={e => setEmergencyContact(e.target.value)}
                placeholder="Name"
              />
            </div>
            <div className="flex-1">
              <label className="block font-semibold text-xs text-blue-800 mb-1">Emergency Number</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-300 transition"
                value={emergencyPhone}
                onChange={e => setEmergencyPhone(e.target.value)}
                placeholder="Phone"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Address Section */}
      <section className="rounded-xl border border-green-100 bg-white shadow-sm p-5 mb-2">
        <div className="mb-2">
          <span className="font-semibold uppercase tracking-wide text-xs text-green-700">Address</span>
        </div>
        <textarea
          className="w-full p-2 border border-gray-200 rounded-lg resize-y min-h-[100px] focus:border-green-400 focus:ring-1 focus:ring-green-300 transition"
          rows={5}
          value={address}
          onChange={e => setAddress(e.target.value)}
          placeholder={"Block/Unit/Street\nTown/City\nPostcode"}
          style={{ minHeight: "100px" }}
        />
      </section>

      {/* Notes Section */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 mb-2">
        <div className="mb-2">
          <span className="font-semibold uppercase tracking-wide text-xs text-gray-700">Notes</span>
        </div>
        <RichTextEditor
          value={notes}
          onChange={setNotes}
          placeholder="Additional notes or information"
          minHeight={120}
        />
      </section>

      {/* Action buttons (bottom right, match SOAP style) */}
      <div className="flex justify-end mt-4 gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow"
        >
          {initialData ? "Update Client" : "Add Client"}
        </button>
      </div>
    </form>
  );
}

export default ClientForm;
